/**
 * Auth Storage Abstraction
 *
 * Web: sessionStorage (per-tab; cleared on tab close) for a tighter posture.
 * Native (Capacitor iOS): localStorage — the WKWebView wipes sessionStorage when
 * the webview is torn down, and the SSO / magic-link deep-link return is a cold
 * relaunch, so a token written during verify would be gone before SessionProvider
 * reads it (the "verify 200, then bounced back to login" loop). See getStorage().
 */

import { isNative } from './capacitor';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';
import { diag } from './diag';

/**
 * Centralized auth-storage key names. Use these constants instead of
 * literal strings so the key name only exists in one place — renaming
 * or rotating becomes a single edit instead of a repo-wide grep.
 */
export const AUTH_STORAGE_KEYS = {
  ACCESS_TOKEN: 'xavior_auth_token',
  REFRESH_TOKEN: 'xavior_refresh_token',
  SESSION_TOKEN: 'sessionToken',
  USER_ID: 'userId',
  USER_EMAIL: 'userEmail',
  SELECTED_CONTAINER: 'xavior_selected_container',
} as const;

/**
 * Global, non-user-scoped sessionStorage flag set when a paywall/checkout gate
 * is voluntarily dismissed (see App.tsx). It is NOT in AUTH_STORAGE_KEYS because
 * it lives only in sessionStorage (never localStorage on native) and must be
 * swept on every auth teardown so a stale flag from one identity can't suppress
 * a gate for the next identity in the same tab.
 */
export const PAYWALL_DISMISSED_KEY = 'xavior:paywall-dismissed';

/**
 * Global, non-user-scoped sessionStorage flag set when the connect-first SSO
 * auto-redirect (PDR 011 / PDR 010 D3) fires once, so a denial that bounces the
 * user back to the connect leaf doesn't re-fire in a loop. Like
 * PAYWALL_DISMISSED_KEY it is NOT user-scoped, so it MUST be swept on every auth
 * teardown — otherwise a flag set during one identity's connect blocks the SSO
 * auto-redirect for the next sign-in in the same tab (it falls back to the
 * manual chooser). The matching helpers live in
 * features/auth/lib/sso-autoconnect.ts (which imports this key).
 */
export const SSO_AUTOCONNECT_ATTEMPTED_KEY = 'xavior:sso-autoconnect-attempted';

/**
 * Get the auth storage. Native (Capacitor) → localStorage (survives the WKWebView
 * teardown / deep-link cold-relaunch); web → sessionStorage (cleared on tab close).
 */
function getStorage(): Storage {
  if (typeof window === 'undefined') {
    // SSR fallback - return a no-op storage
    return {
      length: 0,
      clear: () => {},
      getItem: () => null,
      key: () => null,
      removeItem: () => {},
      setItem: () => {},
    };
  }

  // Native (Capacitor): persist to localStorage so the token survives the
  // WKWebView teardown that the SSO / magic-link deep-link relaunch triggers.
  // Web keeps sessionStorage (per-tab, cleared on close).
  if (isNative()) return localStorage;
  return sessionStorage;
}

export function getAuthItem(key: string): string | null {
  return getStorage().getItem(key);
}

export function setAuthItem(key: string, value: string): void {
  getStorage().setItem(key, value);
}

export function removeAuthItem(key: string): void {
  getStorage().removeItem(key);
}

/** Clear all auth-related items. Call on logout to ensure clean state. */
export function clearAuthStorage(): void {
  const storage = getStorage();
  const keysToRemove = Object.values(AUTH_STORAGE_KEYS);

  keysToRemove.forEach(key => {
    storage.removeItem(key);
  });

  // Also clear from localStorage in case tokens were previously stored there
  keysToRemove.forEach(key => {
    try { localStorage.removeItem(key); } catch { /* ignore */ }
  });

  // The voluntary-dismissal paywall flag is a global sessionStorage key that is
  // never user-scoped — sweep it on teardown so a dismissal by one identity can't
  // suppress a (team-join / checkout) gate for the next identity in the same tab.
  if (typeof window !== 'undefined') {
    try {
      window.sessionStorage.removeItem(PAYWALL_DISMISSED_KEY);
      // Same rationale: the SSO auto-redirect one-shot flag must not survive a
      // teardown, or the next identity's connect-first signup in this tab skips
      // the auto-redirect and lands on the manual chooser.
      window.sessionStorage.removeItem(SSO_AUTOCONNECT_ATTEMPTED_KEY);
    } catch {
      // sessionStorage unavailable (private mode / quota) — best effort.
    }
  }

  // The long-lived refresh token lives in the iOS Keychain (native), not the
  // storage swept above — purge it here so EVERY logout path that clears auth
  // storage (clearSession, PlanGate, …) also drops the refresh token and logout
  // STICKS across relaunch (sprint native-oauth-pass Ch.2, AC 5). Fire-and-forget:
  // the native remove resolves in ms and the server-side revoke (Ch.1 /auth/logout
  // X-Refresh-Token header) is the backstop. No-op on web (helper guards isNative).
  void removeRefreshTokenSecure();

  console.log('[authStorage] Cleared auth storage');
  // #5105: if this fires on every loop cycle, an unauthenticated transition is
  // wiping the session each pass — the diag buffer (xavior:diag:*, not in
  // AUTH_STORAGE_KEYS) deliberately survives this call so the pattern is visible.
  diag('auth', 'clearAuthStorage');
}

/**
 * Native refresh-token storage — iOS Keychain (hardware-backed) via
 * capacitor-secure-storage-plugin (Capacitor iOS · sprint native-oauth-pass Ch.1).
 *
 * The refresh token is long-lived and rotates revoke-before-mint, so it is the
 * one credential that MUST NOT sit in an XSS-readable store (ADR 002 — the app
 * renders untrusted LLM/email output). It therefore never goes through
 * getStorage() (localStorage/sessionStorage) and is deliberately NOT part of
 * AUTH_STORAGE_KEYS, which is the localStorage-backed key set. The access token
 * stays in getStorage() — it is short-lived and already handled there.
 *
 * On web there is no client-side refresh token (the httpOnly cookie owns
 * refresh), so every helper is an inert no-op: the plugin ships an insecure
 * localStorage web fallback that must never be exercised.
 *
 * The Keychain key reuses the AUTH_STORAGE_KEYS.REFRESH_TOKEN string so the key
 * name lives in one place, but the value is only ever read/written here.
 */
const REFRESH_TOKEN_KEYCHAIN_KEY = AUTH_STORAGE_KEYS.REFRESH_TOKEN;

/** Persist the (rotated) refresh token to the iOS Keychain. No-op on web. */
export async function setRefreshTokenSecure(value: string): Promise<void> {
  if (!isNative()) return;
  try {
    await SecureStoragePlugin.set({ key: REFRESH_TOKEN_KEYCHAIN_KEY, value });
  } catch {
    // Best effort — a failed write must not crash the auth flow. Never log the value.
    console.warn('[authStorage] secure refresh-token write failed');
  }
}

/** Read the refresh token from the iOS Keychain. Returns null on web or when absent. */
export async function getRefreshTokenSecure(): Promise<string | null> {
  if (!isNative()) return null;
  try {
    const { value } = await SecureStoragePlugin.get({ key: REFRESH_TOKEN_KEYCHAIN_KEY });
    return value ?? null;
  } catch {
    // The plugin rejects when the key is absent — treat as "no token".
    return null;
  }
}

/** Remove the refresh token from the iOS Keychain. No-op on web. */
export async function removeRefreshTokenSecure(): Promise<void> {
  if (!isNative()) return;
  try {
    await SecureStoragePlugin.remove({ key: REFRESH_TOKEN_KEYCHAIN_KEY });
  } catch {
    // Best effort — absent key / plugin error must not crash teardown.
  }
}

export const authStorageType = isNative() ? 'localStorage' : 'sessionStorage';
